import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { InvestmentViewFormatters } from "@/domain/investment/investmentPieceView";
import {
  formatDateShort,
  formatInteger,
  formatInvestmentReference,
  formatMoney,
  formatMoneyExact,
  formatPercent,
} from "@/utils/formatUtils";
import { quotaName } from "@/utils/quotaName";

const monthIndex = (iso: string) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7));
const dayOfIso = (iso: string) => formatDateShort(`${iso.slice(0, 10)}T12:00:00Z`);

export function useRecordFormat() {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      money: formatMoney,
      amount: formatMoneyExact,
      integer: formatInteger,
      percent: formatPercent,
      reference: formatInvestmentReference,
      date: (iso: string | null | undefined) => formatDateShort(iso),
      day: dayOfIso,
      period: (start: string | null, end: string | null) => {
        if (!start || !end) return start ? dayOfIso(start) : end ? dayOfIso(end) : "";
        return start === end ? dayOfIso(start) : `${dayOfIso(start)} – ${dayOfIso(end)}`;
      },
      duration: (start: string, end: string) => {
        if (start === end) return t("promoters.project.duration_days", { count: 1 });
        const months = monthIndex(end) - monthIndex(start);
        if (months === 0) {
          const days = Number(end.slice(8, 10)) - Number(start.slice(8, 10)) + 1;
          return t("promoters.project.duration_days", { count: days });
        }
        return t("promoters.project.duration_months", { count: months });
      },
    }),
    [t],
  );
}

export function useInvestmentPieceFormatters(): InvestmentViewFormatters {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      quotaName: (quota, incentive) => quotaName(quota, incentive, t),
      currency: formatMoney,
      currencyExact: formatMoneyExact,
      percentage: formatPercent,
      date: (iso: string) => formatDateShort(iso),
      reference: formatInvestmentReference,
    }),
    [t],
  );
}
